/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { I18n } from 'react-polyglot';
import { graphql, useStaticQuery } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';

import MessagesEnglish from '../i18n/en.json';
import MessagesFrench from '../i18n/fr.json';
import MessagesSpanish from '../i18n/es.json';
import { AuthenticationProvider } from './session';
import { withContext } from './Context/Context';
import { withFirebase } from '../firebase';

import 'semantic-ui-css/semantic.min.css';
import '../stylesheets/index.scss';

const Layout = ({ children, context, firebase }) => {
  const intl = useIntl();
  const { locale } = intl;
  moment.locale(locale);

  const { allCoupons: { nodes: coupons }, settings } = useStaticQuery(graphql`
    query MyQuery {
      allTestimonials {
        nodes {
          name
          text {
            fr
            en
            es
          }
          classId
        }
      }
      allCoupons {
        nodes {
          applyTo
          id
          name
          percent
          totalRedeemed
          type
        }
      }
      settings(id: {eq: "mentorshow"}) {
        siteName
        activeCompaign {
          newsletterList
          passClientsList
          passLeadsList
        }
        ctaSticky {
          buttonLabel {
            fr
            en
            es
          }
          text {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        pricing {
          class
          pass
          passPaymentUrl
          thrivePassPaymentUrl {
            fr
            en
            es
          }
          passPaymentUrlGift
          thrivePassPaymentUrlGift {
            fr
            en
            es
          }
          passStripeCouponId
          passStripePlanId
        }
        incentive {
          body {
            fr
            en
            es
          }
          buttonLabel {
            fr
            en
            es
          }
          image {
            src
          }
          linkLabel {
            fr
            en
            es
          }
          pricing {
            fr
            en
            es
          }
          title {
            fr
            en
            es
          }
        }
        id
        landing
        videoPlayer {
          blockedText {
            fr
            en
            es
          }
        }
        marketingAllocationSurvey {
          question {
            fr
            en
            es
          }
          items {
            fr {
              label
            }
            en {
              label
            }
            es {
              label
            }
          }
        }
      }
    }
  `);

  const [, setDeviceOrientation] = React.useState('portrait');

  let passReducedPrice = null;

  const passAppliedCoupon = coupons.find((coupon) => !!coupon.applyTo && coupon.applyTo.indexOf('pass') > -1);

  if (passAppliedCoupon) {
    passReducedPrice = settings.pricing.pass * (1 - (passAppliedCoupon.percent / 100));
  }

  React.useEffect(() => {

    // Store settings in the context
    context.set({
      coupons,
      settings: {
        ...settings,
        pricing: {
          ...settings.pricing,
          passReducedPrice,
        },
      },
    });
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    const orientationChangeCallback = () => {
      if (window.matchMedia('(orientation: portrait)').matches) {
        setDeviceOrientation('portrait');
      }

      if (window.matchMedia('(orientation: landscape)').matches) {
        setDeviceOrientation('landscape');
      }
    };

    window.addEventListener('orientationchange', orientationChangeCallback);

    return () => {
      window.removeEventListener('orientationchange', orientationChangeCallback);
    };
  }, []);

  React.useEffect(() => {
    const getRatings = async () => {
      const globalRatingsSnapshot = await firebase.globalRating().get();
      const globalRatingsData = globalRatingsSnapshot.data();

      context.set({
        rating: {
          ...globalRatingsData,
          average: Math.round(((globalRatingsData.totalPoints / globalRatingsData.nbRatings) + Number.EPSILON) * 10) / 10, // round to nearest 1 decimal or int
        },
      });
    };

    if (firebase) {
      getRatings();
    }
  }, [firebase]);

  let messages = null;

  switch (locale) {
    case 'fr':
      messages = MessagesFrench;
      break;
    case 'en':
      messages = MessagesEnglish;
      break;
    case 'es':
      messages = MessagesSpanish;
      break;
    default:
      messages = MessagesFrench;
      break;
  }

  return (
    <I18n locale={locale} messages={messages} allowMissing>
      <AuthenticationProvider>
        <div className="app">
          <main>{children}</main>
        </div>
      </AuthenticationProvider>
    </I18n>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  context: PropTypes.object.isRequired,
  firebase: PropTypes.object,
};

Layout.defaultProps = {
  firebase: null,
};

export default withContext(
  withFirebase(
    Layout,
  ),
);
